// PUBLIC customer document page (/d/{token}) — what a customer sees when they open a quote /
// invoice link from an email. Modelled on Xero's online invoice/quote pages: a slim action bar
// over a paper-like document, single column on phones. No login — the token in the URL is the
// whole authorisation, so (like portal.js) this page uses plain fetch: the api client's auth
// machinery would bounce an anonymous customer to /login. Copy is plain English on purpose —
// the i18n bundle belongs to the company's own users, not the customers they invoice.
//
// Contract (workstream A phase 7, built in lockstep with the API sibling):
//   GET  /api/public/doc/{token}          → { doc: {...} }        404 → invalid-link page
//   POST /api/public/doc/{token}/approve  → { ok, accepted_at }   409 → already decided (re-fetch)
//   POST /api/public/doc/{token}/decline  → { ok, declined_at }   409 → same
//   GET  /api/public/doc/{token}/pdf      → application/pdf       (plain link, no bearer)
//   POST /api/public/doc/{token}/pay      → { redirect_url, fields } → hidden-form POST to PayFast
import { html, signal } from '../shared/t4.js';
import { remittanceRows } from '../shared/remittance.js';

// ── pure helpers (exported for tests) ───────────────────────────────────────────────────────

export const DOC_LABEL = {
  QUO: 'Quote', INV: 'Invoice', CRN: 'Credit Note', SINV: 'Supplier Invoice',
  PORD: 'Purchase Order', GRV: 'Goods Receipt', RTS: 'Supplier Credit Note', ORD: 'Sales Order',
};
export function docLabel(code){ return DOC_LABEL[code] || 'Document'; }

// Mirrors documents.js's dueLabelKey so the customer reads the same word the tenant app uses.
export function dueLabel(code){
  if(code === 'INV' || code === 'SINV') return 'Due date';
  if(code === 'PORD' || code === 'GRV' || code === 'RTS') return 'Expected date';
  return 'Expiry date';
}

// "1 234.56" — space-grouped thousands, always two decimals (SA convention).
export function fmtAmount(n){
  const v = Number(n || 0);
  const parts = Math.abs(v).toFixed(2).split('.');
  return (v < 0 ? '-' : '') + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + '.' + parts[1];
}

// "R 1 234.56" for ZAR, "USD 1 234.56" for anything else; the bare figure when the currency is
// unknown — never an assumed rand (owner 2026-10-19).
export function fmtMoney(n, currency){
  const cur = String(currency || '').toUpperCase();
  return (cur ? (cur === 'ZAR' ? 'R' : cur) + ' ' : '') + fmtAmount(n);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// "2 Sep 2026" from an ISO date or datetime; anything unparseable comes back as-is.
export function fmtDate(s){
  if(!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if(!m) return String(s);
  return Number(m[3]) + ' ' + MONTHS[Number(m[2]) - 1] + ' ' + m[1];
}

export function isVoidStatus(status){
  const s = String(status || '').toLowerCase();
  return s === 'void' || s === 'cancelled';
}

// A quote can be approved/declined only once it is confirmed (numbered — not a draft), is not
// void, and the customer has not already decided.
export function canDecide(d){
  if(!d || d.type_code !== 'QUO') return false;
  const s = String(d.status || '').toLowerCase();
  if(s === 'draft' || isVoidStatus(s)) return false;
  const cs = d.customer_state;
  return !(cs && (cs.accepted_at || cs.declined_at));
}

const INVALID_MSG = 'This link is no longer valid.';

function pct(v){ return (v === null || v === undefined || v === '') ? '—' : Number(v) + '%'; }
function qtyText(v){ return String(Number(v || 0)); }

// ── the screen ──────────────────────────────────────────────────────────────────────────────

export function publicDocScreen(token){
  const base = '/api/public/doc/' + encodeURIComponent(token);

  const doc = signal(null), loading = signal(true), err = signal('');
  const acting = signal(false), actErr = signal('');
  const payAmount = signal('');   // "Amount to pay now" — defaults to the full balance on load
  const declineMode = signal(false);
  const fName = signal(''), fComment = signal(''), fReason = signal('');
  // ── customer questions (G2 public Q&A) ──────────────────────────────────────────────────────
  // FOCUS: the question name/text signals (qName/qText) are bound with `.value=${sig}` but are
  // NEVER read at the top of body() below — exactly like fName/fComment/fReason — so a keystroke
  // updates only that one input property and never re-runs (and so never recreates) the body slot.
  // The signals body() DOES read (questions/qBusy/qErr/qSent) change only on discrete clicks/loads.
  const questions = signal([]), qName = signal(''), qText = signal('');
  const qBusy = signal(false), qErr = signal(''), qSent = signal(false);

  async function load(){
    try {
      const res = await fetch(base);
      const body = await res.json().catch(() => ({}));
      if(res.status === 404) throw new Error(INVALID_MSG);
      if(!res.ok) throw new Error((body && body.error) || INVALID_MSG);
      doc.value = body.doc || null;
      // default the "pay now" amount to the full balance (the customer can lower it to part-pay)
      const bal = doc.value && doc.value.totals && Number(doc.value.totals.balance_due || 0);
      if(bal > 0) payAmount.value = bal.toFixed(2);
      err.value = '';
    } catch(e){ err.value = (e && e.message) || INVALID_MSG; }
    loading.value = false;
  }
  load();

  // Questions load in parallel with the document — failure-tolerant: an invalid/older link (or a
  // backend without the Q&A endpoint yet) simply leaves the list empty, never breaking the view.
  async function loadQuestions(){
    try {
      const res = await fetch(base + '/questions');
      if(!res.ok){ questions.value = []; return; }
      const body = await res.json().catch(() => ({}));
      questions.value = Array.isArray(body && body.data) ? body.data : [];
    } catch { questions.value = []; }
  }
  loadQuestions();

  // Post a customer question. Optional name goes through only when filled. On success the returned
  // question is appended locally (no second round-trip) and the form clears — this repaints body()
  // (questions/qSent changed) but only on this discrete click, never mid-keystroke.
  async function sendQuestion(){
    if(qBusy.value) return;
    qErr.value = '';
    const bodyText = qText.value.trim();
    if(!bodyText){ qErr.value = 'Type your question first.'; return; }
    qBusy.value = true; qErr.value = ''; qSent.value = false;
    const payload = { body: bodyText };
    if(qName.value.trim()) payload.name = qName.value.trim();
    try {
      const res = await fetch(base + '/questions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload) });
      const out = await res.json().catch(() => ({}));
      if(!res.ok) throw new Error((out && out.error) || 'Could not send your question — please try again.');
      const q = (out && out.question) || { body: bodyText, author_name: payload.name || null,
        answer: null, answered_at: null, created_at: null };
      questions.value = [...(questions.value || []), q];
      qText.value = ''; qName.value = '';
      qSent.value = true;
    } catch(e){ qErr.value = (e && e.message) || 'Could not send your question — please try again.'; }
    qBusy.value = false;
  }

  // Approve / decline a quote. Optional fields go through only when filled in. A 409 means the
  // decision was already recorded (this tab raced another) — re-fetch and show the decided state.
  async function decide(kind){
    if(acting.value) return;
    acting.value = true; actErr.value = '';
    const body = {};
    if(fName.value.trim()) body.name = fName.value.trim();
    if(kind === 'approve' && fComment.value.trim()) body.comment = fComment.value.trim();
    if(kind === 'decline' && fReason.value.trim()) body.reason = fReason.value.trim();
    try {
      const res = await fetch(base + '/' + kind, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body) });
      const out = await res.json().catch(() => ({}));
      if(res.ok || res.status === 409){          // 409 = already decided: the re-fetch shows by whom
        declineMode.value = false;
        await load();
      } else {
        throw new Error((out && out.error) || 'Could not record your decision — please try again.');
      }
    } catch(e){ actErr.value = (e && e.message) || 'Could not record your decision — please try again.'; }
    acting.value = false;
  }

  // PayFast hand-off: the API signs the field set; we POST the browser there via a hidden form
  // (same pattern as tenant billing's startPayfastCheckout). On success the browser navigates
  // away, so `acting` deliberately stays true — no double-pay window from a second click.
  async function payNow(){
    if(acting.value) return;
    acting.value = true; actErr.value = '';
    try {
      // an "Amount to pay now" below the full balance → a part-payment; blank/full → the whole balance
      const d = doc.value || {}, bal = Number((d.totals && d.totals.balance_due) || 0);
      const chosen = Number(payAmount.value);
      const payBody = (chosen > 0 && chosen < bal - 0.005) ? { amount: Math.round(chosen * 100) / 100 } : {};
      const res = await fetch(base + '/pay', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payBody) });
      const out = await res.json().catch(() => ({}));
      if(!res.ok) throw new Error((out && out.error) || 'Online payment is unavailable right now.');
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = out.redirect_url;
      form.className = 'pd-payform';
      Object.entries(out.fields || {}).forEach(([k, v]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = k; input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch(e){
      actErr.value = (e && e.message) || 'Could not start the payment.';
      acting.value = false;
    }
  }

  function enterDecline(){ declineMode.value = true; actErr.value = ''; }
  function exitDecline(){ declineMode.value = false; actErr.value = ''; }
  // Focus-safe stale-banner clear: body() reads actErr/qErr at its top, so a signal write here would
  // re-run the whole public view and rebuild the input being typed into (the tina4 focus-trap). We
  // instead drop the inline .pd-err node directly; the save functions reset their err signal before
  // re-validating, so a recurring identical error still re-shows.
  const dropErrIn = (e, sel) => { const c = e.target.closest(sel); const el = c && c.querySelector('.pd-err'); if(el) el.remove(); };

  // ONE reactive body — every signal is read at the top and used as a plain value below (no
  // nested `${() => …}` slots: they append duplicates on re-render, per the validated gotcha).
  const body = () => {
    if(loading.value) return html`<div class="pd-card pd-loading">Loading document…</div>`;

    if(err.value){
      const detail = err.value !== INVALID_MSG ? err.value
        : 'The document may have been withdrawn or the link may have expired.';
      return html`<div class="pd-card pd-invalid">
        <div class="pd-invalid-icon">🔗</div>
        <h1 class="pd-invalid-title">${INVALID_MSG}</h1>
        <p class="pd-invalid-sub">${detail}</p>
        <p class="pd-invalid-sub">Please ask the sender for a fresh link.</p>
      </div>`;
    }

    const d = doc.value || {};
    const org = d.org || {}, contact = d.contact || {}, totals = d.totals || {};
    const cs = d.customer_state || null;
    const status = String(d.status || '').toLowerCase();
    const voided = isVoidStatus(status);
    const draft = status === 'draft';
    const cur = d.currency || '';
    const balance = Number(totals.balance_due || 0);
    const isInv = d.type_code === 'INV';
    const paid = isInv && !voided && totals.balance_due !== undefined && totals.balance_due !== null && balance <= 0;
    const accepted = !!(cs && cs.accepted_at);
    const declined = !!(cs && cs.declined_at);
    const showCard = canDecide(d);
    const payable = isInv && !voided && !draft && !!(d.pay && d.pay.enabled) && balance > 0;
    // Feature 1.2/1.3 — branding (logo + accent) + country-aware remittance from the org payload
    const rawAccent = (org.brand_color || '').trim();
    const accent = /^#[0-9a-fA-F]{3,8}$/.test(rawAccent) ? rawAccent : '';   // hex only — never inject CSS
    const logo = (org.logo || '').trim();
    const payRows = remittanceRows({ remittance: org.banking, iso2: org.country_iso2 });
    const showBanking = isInv && !voided && !draft && payRows.length > 0 && balance > 0;
    // quote deposit (invoiceTest parity): after acceptance the customer can pay the deposit online
    const dep = d.deposit || {};
    const depAsk = d.type_code === 'QUO' && Number(dep.amount) > 0 && !dep.paid;
    const depPayable = d.type_code === 'QUO' && !!dep.payable;
    const depPaid = d.type_code === 'QUO' && !!dep.paid;
    const busy = acting.value;
    const dm = declineMode.value;
    const aerr = actErr.value;
    const lines = Array.isArray(d.lines) ? d.lines : [];
    const orgName = org.name || 'the sender';
    // Q&A signals read HERE (top) as plain values — NEVER qName/qText (their inputs must keep focus).
    const qs = Array.isArray(questions.value) ? questions.value : [];
    const qbusy = qBusy.value, qerr = qErr.value, qsent = qSent.value;
    const showQna = !!d.type_code && !voided && !draft;   // a real, shared, live document only

    return html`<div class=${'pd-wrap' + (accent ? ' pd-branded' : '')} style=${accent ? '--doc-accent:' + accent : ''}>

      <div class="pd-bar">
        <div class="pd-bar-id">
          <div class="pd-org-name">${org.name || ''}</div>
          <div class="pd-doc-id">${docLabel(d.type_code)} <span class="pd-doc-no">${d.number || ''}</span>
            ${paid ? html`<span class="pd-pill pd-pill-paid">PAID</span>` : ''}
            ${draft ? html`<span class="pd-pill pd-pill-draft">DRAFT</span>` : ''}</div>
        </div>
        <div class="pd-bar-actions">
          ${d.pdf ? html`<a class="btn btn-outline pd-pdf-link" href=${base + '/pdf'} target="_blank" rel="noopener">⬇ PDF</a>` : ''}
          ${payable ? html`<button class="btn btn-primary pd-pay-btn" ?disabled=${busy} @click=${payNow}>${busy ? 'Starting payment…' : 'Pay now'}</button>` : ''}
        </div>
      </div>

      ${voided ? html`<div class="pd-banner pd-banner-void">This document has been voided.</div>` : ''}
      ${accepted ? html`<div class="pd-banner pd-banner-ok">✓ Accepted${cs.accepted_name ? ' by ' + cs.accepted_name : ''} on ${fmtDate(cs.accepted_at)}</div>` : ''}
      ${declined ? html`<div class="pd-banner pd-banner-bad">Declined on ${fmtDate(cs.declined_at)}${cs.decline_reason ? ' — ' + cs.decline_reason : ''}</div>` : ''}
      ${payable ? html`<div class="pd-pay-card">
        <div class="pd-pay-title">Pay this invoice</div>
        <div class="pd-pay-row">
          <label class="pd-pay-lbl" for="pd-pay-amt">Amount to pay now</label>
          <input id="pd-pay-amt" class="form-control pd-pay-input" type="number" step="0.01" min="0" max=${String(balance)}
            .value=${payAmount} @input=${e => { payAmount.value = e.target.value; dropErrIn(e, '.pd-pay-card'); }}>
          <button class="btn btn-primary" ?disabled=${busy} @click=${payNow}>${busy ? 'Starting payment…' : 'Pay now'}</button>
        </div>
        <div class="pd-pay-hint">Balance due ${fmtMoney(balance, cur)} — pay it in full, or enter a smaller amount to part-pay.</div>
        ${aerr ? html`<div class="pd-err pd-pay-err">${aerr}</div>` : ''}
      </div>` : ''}
      ${aerr && !showCard ? html`<div class="pd-banner pd-banner-bad">${aerr}</div>` : ''}

      ${depPaid ? html`<div class="pd-banner pd-banner-ok">✓ Deposit of ${fmtMoney(dep.paid_amount, cur)} received — thank you. ${orgName} will convert this to an invoice.</div>` : ''}
      ${accepted && depPayable ? html`<div class="pd-pay-card">
        <div class="pd-pay-title">One step left — secure it with the deposit</div>
        <div class="pd-pay-hint">A ${dep.mode === 'amount' ? '' : Number(dep.pct) + '% '}deposit of <b>${fmtMoney(dep.amount, cur)}</b> secures this work — it is held on your account and comes straight off the invoice.</div>
        <div class="pd-pay-row">
          <button class="btn btn-primary" ?disabled=${busy} @click=${payNow}>${busy ? 'Starting payment…' : '💳 Pay the deposit now'}</button>
        </div>
        ${aerr ? html`<div class="pd-err pd-pay-err">${aerr}</div>` : ''}
        <div class="pd-pay-hint">🔒 Payments secured by PayFast</div>
      </div>` : ''}

      ${showCard ? html`<div class="pd-card pd-decide">
        <div class="pd-decide-title">${dm ? 'Decline this quote' : 'Approve this quote'}</div>
        <div class="pd-decide-sub">${dm
          ? 'Let ' + orgName + ' know why — they may be able to requote.'
          : (depAsk
              ? 'Happy with it? Approve below, then secure the work with a ' + (dep.mode === 'amount' ? '' : Number(dep.pct) + '% ') + 'deposit of ' + fmtMoney(dep.amount, cur) + '.'
              : 'Happy with it? Approve below and ' + orgName + ' will get to work.')}</div>
        <label class="pd-field"><span class="pd-lbl">Your name (optional)</span>
          <input class="form-control" .value=${fName} @input=${e => { fName.value = e.target.value; dropErrIn(e, '.pd-decide'); }}
            placeholder="e.g. Jane Smith"></label>
        ${dm
          ? html`<label class="pd-field"><span class="pd-lbl">Reason</span>
              <textarea class="form-control" rows="3" .value=${fReason} @input=${e => { fReason.value = e.target.value; dropErrIn(e, '.pd-decide'); }}
                placeholder="e.g. We've gone with another supplier"></textarea></label>`
          : html`<label class="pd-field"><span class="pd-lbl">Comment (optional)</span>
              <textarea class="form-control" rows="2" .value=${fComment} @input=${e => { fComment.value = e.target.value; dropErrIn(e, '.pd-decide'); }}
                placeholder="e.g. Please start after the 15th"></textarea></label>`}
        ${aerr ? html`<div class="pd-err">${aerr}</div>` : ''}
        <div class="pd-decide-actions">
          ${dm
            ? html`<button class="btn btn-outline" ?disabled=${busy} @click=${exitDecline}>Back</button>
                   <button class="btn btn-danger" ?disabled=${busy} @click=${() => decide('decline')}>${busy ? 'Sending…' : 'Confirm decline'}</button>`
            : html`<button class="btn btn-outline" ?disabled=${busy} @click=${enterDecline}>Decline</button>
                   <button class="btn btn-primary" ?disabled=${busy} @click=${() => decide('approve')}>${busy ? 'Sending…' : (depAsk ? 'Approve — then pay the deposit' : 'Approve')}</button>`}
        </div>
      </div>` : ''}

      <div class="pd-card pd-paper">
        <div class="pd-paper-head">
          <div class="pd-org">
            ${logo ? html`<img class="pd-org-logo" src=${logo} alt=${org.name || ''}>` : ''}
            <div class="pd-org-co">${org.name || ''}</div>
            ${org.address ? html`<div class="pd-org-line">${org.address}</div>` : ''}
            ${org.phone ? html`<div class="pd-org-line">${org.phone}</div>` : ''}
            ${org.email ? html`<div class="pd-org-line">${org.email}</div>` : ''}
            ${org.vat_no ? html`<div class="pd-org-line">VAT no ${org.vat_no}</div>` : ''}
          </div>
          <div class="pd-meta">
            <div class="pd-meta-type">${docLabel(d.type_code)}</div>
            <div class="pd-meta-no">${d.number || ''}</div>
            <div class="pd-meta-row"><span class="pd-lbl">Date</span> ${fmtDate(d.date)}</div>
            ${d.due_date ? html`<div class="pd-meta-row"><span class="pd-lbl">${dueLabel(d.type_code)}</span> ${fmtDate(d.due_date)}</div>` : ''}
          </div>
        </div>

        <div class="pd-billto">
          <div class="pd-lbl">Bill to</div>
          <div class="pd-billto-name">${contact.name || '—'}</div>
          ${contact.email ? html`<div class="pd-org-line">${contact.email}</div>` : ''}
        </div>

        <div class="pd-tbl-wrap">
          <table class="pd-lines">
            <thead><tr><th>Description</th><th class="r">Qty</th><th class="r">Price</th>
              <th class="r">Disc %</th><th class="r">Tax %</th><th class="r">Total</th></tr></thead>
            <tbody>${lines.map(l => html`<tr>
              <td>${l.description || ''}</td>
              <td class="r">${qtyText(l.qty)}</td>
              <td class="r">${fmtAmount(l.unit_price)}</td>
              <td class="r">${Number(l.discount_pc) ? Number(l.discount_pc) + '%' : '—'}</td>
              <td class="r">${pct(l.tax_rate)}</td>
              <td class="r">${fmtAmount(l.line_total)}</td></tr>`)}</tbody>
          </table>
        </div>

        <div class="pd-totals">
          <div class="pd-tot-row"><span>Sub-total</span><span>${fmtMoney(totals.sub_total, cur)}</span></div>
          ${Number(totals.invoice_discount) > 0 ? html`<div class="pd-tot-row"><span>Invoice discount</span><span>−${fmtMoney(totals.invoice_discount, cur)}</span></div>` : ''}
          <div class="pd-tot-row"><span>Tax</span><span>${fmtMoney(totals.tax_total, cur)}</span></div>
          <div class="pd-tot-row pd-tot-grand"><span>Total</span><span>${fmtMoney(totals.total, cur)}</span></div>
          ${d.type_code === 'QUO' && Number(dep.amount) > 0 ? html`<div class="pd-tot-row pd-tot-balance"><span>Deposit due at acceptance (${Number(dep.pct)}%)</span><span>${fmtMoney(dep.amount, cur)}</span></div>` : ''}
          ${isInv ? html`<div class="pd-tot-row"><span>Amount paid</span><span>${fmtMoney(totals.amount_paid, cur)}</span></div>
            <div class="pd-tot-row pd-tot-balance"><span>Balance due</span><span>${fmtMoney(totals.balance_due, cur)}</span></div>` : ''}
        </div>
      </div>

      ${showBanking ? html`<div class="pd-card pd-paybox">
        <div class="pd-paybox-title">How to pay</div>
        <div class="pd-pay-rows">
          ${payRows.map(r => html`<div class="pd-pay-row"><span class="pd-lbl">${r.label}</span><span class=${r.mono ? 'pd-mono' : ''}>${r.value}</span></div>`)}
          <div class="pd-pay-row"><span class="pd-lbl">Reference</span><span class="pd-mono">${d.number || ''}</span></div>
        </div>
        <div class="pd-pay-note">Please use the reference so ${orgName} can match your payment.</div>
      </div>` : ''}

      ${showQna ? html`<div class="pd-card pd-qna">
        <div class="pd-qna-title">Questions</div>
        <div class="pd-qna-sub">Ask ${orgName} anything about this ${docLabel(d.type_code).toLowerCase()} — the reply appears here.</div>
        ${qs.length ? html`<div class="pd-qna-list">${qs.map(q => html`<div class="pd-qna-item">
          <div class="pd-qna-msg pd-qna-ask-msg">
            <div class="pd-qna-meta"><span class="pd-qna-who">${q.author_name || 'You'}</span>${q.created_at ? html`<span class="pd-qna-when">${fmtDate(q.created_at)}</span>` : ''}</div>
            <div class="pd-qna-body">${q.body || ''}</div>
          </div>
          ${q.answer
            ? html`<div class="pd-qna-msg pd-qna-reply-msg">
                <div class="pd-qna-meta"><span class="pd-qna-who">${orgName}</span>${q.answered_at ? html`<span class="pd-qna-when">${fmtDate(q.answered_at)}</span>` : ''}</div>
                <div class="pd-qna-body">${q.answer}</div></div>`
            : html`<div class="pd-qna-pending">Awaiting a reply from ${orgName}.</div>`}
        </div>`)}</div>` : ''}
        ${qsent ? html`<div class="pd-qna-sent">✓ Your question was sent to ${orgName}. They'll get back to you.</div>` : ''}
        <div class="pd-qna-ask">
          <div class="pd-qna-ask-title">${qs.length ? 'Ask another question' : 'Ask a question'}</div>
          <label class="pd-field"><span class="pd-lbl">Your name (optional)</span>
            <input class="form-control" .value=${qName} @input=${e => { qName.value = e.target.value; dropErrIn(e, '.pd-qna-ask'); }}
              placeholder="e.g. Thabo Mokoena"></label>
          <label class="pd-field"><span class="pd-lbl">Your question</span>
            <textarea class="form-control pd-qna-text" rows="3" .value=${qText} @input=${e => { qText.value = e.target.value; dropErrIn(e, '.pd-qna-ask'); }}
              placeholder="e.g. Can this be delivered before the 15th? Is installation included?"></textarea></label>
          ${qerr ? html`<div class="pd-err">${qerr}</div>` : ''}
          <div class="pd-qna-actions">
            <button class="btn btn-primary pd-qna-send" ?disabled=${qbusy} @click=${sendQuestion}>${qbusy ? 'Sending…' : 'Send question'}</button>
          </div>
        </div>
      </div>` : ''}

      <div class="pd-foot">Powered by <span class="pd-foot-brand">aboo</span></div>
    </div>`;
  };

  return html`<div class="pd-page">${body}</div>`;
}
